<template>
    <aside class="nav-new-drawer">
        <header class="drawer-header">
            <div class="drawer-title-group">
                <Icon :name="navItem?.icon?.name" class="drawer-title-icon" />
                <h2 class="drawer-title">{{ navItem?.label }}</h2>
            </div>
            <button class="drawer-close-btn" @click="$emit('close')" title="Close drawer">
                <Icon name="chevron-left" />
            </button>
        </header>
        <div class="drawer-content">
            <button class="drawer-nav-btn" @click="$emit('commit', navItem)">
                {{ navItem?.label }}
            </button>
            <!-- Temporary Teleport target for grid controls — remove when action component is built -->
            <p class="drawer-section-header" v-show="!hasDrawerContent && hasGridControls">Grid Controls</p>
            <div id="nav-drawer-grid-controls" ref="gridControlsRef" v-show="!hasDrawerContent"></div>
            <slot name="drawer-content"></slot>
        </div>
    </aside>
</template>

<script setup>
import { ref, onMounted, onUnmounted } from 'vue';
import Icon from '@common/icon/Icon.vue';

defineProps({
    navItem: {
        type: Object,
        default: null
    },
    hasDrawerContent: {
        type: Boolean,
        default: false
    }
});

defineEmits(['close', 'commit']);

const gridControlsRef = ref(null);
const hasGridControls = ref(false);
let observer = null;

onMounted(() => {
    if (gridControlsRef.value) {
        hasGridControls.value = gridControlsRef.value.childElementCount > 0;
        observer = new MutationObserver(() => {
            hasGridControls.value = gridControlsRef.value.childElementCount > 0;
        });
        observer.observe(gridControlsRef.value, { childList: true });
    }
});

onUnmounted(() => {
    observer?.disconnect();
});
</script>

<style scoped>
.nav-new-drawer {
    width: 100%;
    height: 100%;
    display: flex;
    flex-direction: column;
    animation: slideIn 0.18s ease-out;
}

.drawer-nav-btn {
    display: flex;
    align-items: center;
    width: 100%;
    padding: 0.5rem 0.75rem;
    border: none;
    background: none;
    cursor: pointer;
    text-align: left;
    font-size: var(--text-size-base);
    color: #111827;
    border-radius: 6px;
    transition: background-color 0.15s ease;
}

.drawer-nav-btn:hover {
    background-color: #f3f4f6;
}

.drawer-section-header {
    margin: 1rem 0 0.25rem;
    padding: 0 0.75rem;
    font-size: var(--text-size-subtitle);
    font-weight: var(--text-weight-light);
    color: var(--text-color-muted);
    text-transform: uppercase;
    letter-spacing: 0.05em;
}

:deep(.drawer-nav-button button) {
    display: flex;
    align-items: center;
    justify-content: flex-start;
    width: 100%;
    padding: 0.5rem 0.75rem;
    border: none;
    background: none;
    cursor: pointer;
    text-align: left;
    font-size: var(--text-size-base);
    color: #111827;
    border-radius: 6px;
    transition: background-color 0.15s ease;
    box-shadow: none;
}

:deep(.drawer-nav-button button:hover) {
    background-color: #f3f4f6;
    box-shadow: none;
}

.drawer-content {
    flex: 1;
    overflow-y: auto;
    padding: 0.75rem 1rem 1rem;
    min-height: 0;
}

.drawer-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 0.75rem 1rem;
    border-bottom: 1px solid #e0e0e0;
}

.drawer-title-group {
    display: inline-flex;
    align-items: center;
    gap: 0.5rem;
}

.drawer-title-icon {
    color: #6b7280;
}

.drawer-title {
    margin: 0;
    font-size: 1rem;
    font-weight: 600;
    color: #111827;
}

.drawer-close-btn {
    border: none;
    background: none;
    cursor: pointer;
    color: #6b7280;
    border-radius: 999px;
    width: 28px;
    height: 28px;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: background-color 0.15s ease, color 0.15s ease;
}

.drawer-close-btn:hover {
    background-color: #f3f4f6;
    color: #111827;
}

@keyframes slideIn {
    from {
        transform: translateX(-8px);
        opacity: 0;
    }
    to {
        transform: translateX(0);
        opacity: 1;
    }
}

:deep(.drawer-nav-button button span::before) {
    margin-left: var(--space-1);
}
</style>
